// DataTableHeaderOptions.jsx
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '@mui/material/Icon';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';

const DataTableHeaderOptions = ({
  createNewTitle,
  currentPageData,
  dataTableData,
  onApplyFilters,
  getData,
  CreateNewModal,
  FilterModal,
  ModalDownloadData,
  selectedDocument,
  setSelectedDocument,
  onUploadConsolidado,
  initialFilters,
  // 👇 nuevo prop para ocultar totalmente el botón (y el modal) de creación
  hideCreate = false,
}) => {
  const location = useLocation();
  const [isModalFiltersOpen, setIsModalFiltersOpen] = useState(false);
  const [isCreateNewModalOpen, setIsCreateNewModalOpen] = useState(false);
  const [isModalDownloadDataOpen, setIsModalDownloadDataOpen] = useState(false);

  const activeFilters = initialFilters || {};

  // ¿Hay filtros activos?
  const hasActiveFilters = Object.entries(activeFilters).some(
    ([, value]) => value !== null && value !== ''
  );

  const handleExcelExport = () => {
    const allRows = dataTableData?.rows || [];
    const data = allRows.map(row => {
      const newRow = {};
      Object.keys(row).forEach(key => {
        if (key === 'acciones') return;
        const value = Array.isArray(row[key]) ? row[key][0] : row[key];
        const header = key.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
        newRow[header] = value;
      });
      return newRow;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `Export-${new Date().toISOString()}.xlsx`);
  };

  // 🔒 helper: permitido renderizar bloque de "Nueva Orden"
  const canRenderCreate = !hideCreate && !!CreateNewModal && !!createNewTitle;

  return (
    <SoftBox display="flex" flexDirection="column" mb={2}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        {/* ✅ Botón "Nueva Orden" SOLO si está permitido explícitamente */}
        {canRenderCreate ? (
          <SoftButton
            variant="gradient"
            color="info"
            onClick={() => setIsCreateNewModalOpen(true)}
            data-testid="create-new-button"
          >
            {createNewTitle}
          </SoftButton>
        ) : (
          // placeholder opcional para mantener layout; si no lo quieres, quita este SoftBox
          <SoftBox />
        )}

        <SoftBox display="flex" alignItems="center">
          <SoftButton
            variant="gradient"
            color={hasActiveFilters ? 'primary' : 'warning'}
            onClick={() => setIsModalFiltersOpen(true)}
          >
            {hasActiveFilters ? 'Filtros aplicados' : 'Filtrar'}
          </SoftButton>

          <SoftBox ml={1}>
            <SoftButton variant="gradient" color="success" onClick={handleExcelExport}>
              <Icon>download</Icon>&nbsp;Exportar Excel
            </SoftButton>
          </SoftBox>

          {location.pathname === '/finanzas/consolidados' && (
            <SoftBox ml={1}>
              <SoftButton
                variant="gradient"
                color="primary"
                onClick={() => setIsModalDownloadDataOpen(true)}
              >
                <Icon>download</Icon>&nbsp;Reporte de Liquidaciones
              </SoftButton>
            </SoftBox>
          )}

          {/* ✅ Modal de creación SOLO si está permitido */}
          {canRenderCreate && (
            <CreateNewModal
              getData={getData}
              document={selectedDocument}
              show={isCreateNewModalOpen || selectedDocument}
              handleClose={() => {
                if (selectedDocument) setSelectedDocument?.(null);
                setIsCreateNewModalOpen(false);
              }}
              onUploadConsolidado={onUploadConsolidado}
            />
          )}

          {FilterModal && (
            <FilterModal
              key={JSON.stringify(activeFilters)}
              show={isModalFiltersOpen}
              onApplyFilters={onApplyFilters}
              handleClose={() => setIsModalFiltersOpen(false)}
              initialFilters={activeFilters}
              dataTableData={dataTableData}
              currentPageData={currentPageData}
            />
          )}

          {ModalDownloadData && (
            <ModalDownloadData
              show={isModalDownloadDataOpen}
              handleClose={() => setIsModalDownloadDataOpen(false)}
              onApplyFilters={onApplyFilters}
            />
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

DataTableHeaderOptions.propTypes = {
  createNewTitle: PropTypes.string,
  currentPageData: PropTypes.object,
  dataTableData: PropTypes.object,
  onApplyFilters: PropTypes.func,
  selectedDocument: PropTypes.object,
  setSelectedDocument: PropTypes.func,
  getData: PropTypes.func,
  CreateNewModal: PropTypes.elementType,
  FilterModal: PropTypes.elementType,
  ModalDownloadData: PropTypes.elementType,
  onUploadConsolidado: PropTypes.func,
  initialFilters: PropTypes.object,
  hideCreate: PropTypes.bool, // 👈 nuevo
};

export default DataTableHeaderOptions;
